import { Stack } from "@mui/material";
import type { FC } from "react";
import {
	ClientPropertyCategoryEnum,
	type ClientPropertyCottageDetailResponseDTO,
	ClientPropertyOwnershipEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailNote } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailNote";
import { DetailRow } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailRow";
import { PropertyValueRows } from "web_myplann/Portfolio/Components/Redesign/Detail/PropertyValueRows";
import { BUILDING_OPTIONAL_PARAMS } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/Property/buildingOptionalParams";
import { PropertyExtendedParams } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/Property/PropertyExtendedParams";
import { PropertyPurchaseDetails } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/Property/PropertyPurchaseDetails";
import { formatEnumLabel } from "web_myplann/Portfolio/Helpers/formatEnumLabel";
import {
	getBuildingMaterial,
	getDisposition,
} from "web_myplann/Portfolio/Helpers/propertyBuildingAttributes";
import { useGetPropertyOptionalParams } from "web_myplann/Portfolio/Hooks/useGetPropertyOptionalParams";
import { unitFormatter } from "web_myplann/Shared/Utils/numberFormatters";
import { UNITS } from "web_myplann/Shared/Utils/units";

const PageResources = Resources.PortfolioDetail;

type Props = {
	data: ClientPropertyCottageDetailResponseDTO;
	currencySign: string;
};

export const CottageRedesign: FC<Props> = ({ data, currencySign }) => {
	const { t } = useResource();

	const { properties } = useGetPropertyOptionalParams(
		BUILDING_OPTIONAL_PARAMS,
		data,
		t,
	);

	const ownership = formatEnumLabel(
		data.ownership,
		ClientPropertyOwnershipEnum.None,
		PageResources.Property.OwnershipValue,
		t,
	);

	const buildingMaterial = getBuildingMaterial(data, t);
	const disposition = getDisposition(data, t);

	const propertyCategory = formatEnumLabel(
		data.propertyCategory,
		ClientPropertyCategoryEnum.None,
		PageResources.Property.PropertyCategoryValue,
		t,
	);

	return (
		<>
			<Stack bgcolor="background.paper">
				<DetailRow
					label={t(PageResources.Property.TypeOfConstruction)}
					value={propertyCategory}
				/>
				<DetailRow
					label={t(PageResources.Property.Disposition)}
					value={disposition}
				/>
				<DetailRow
					label={t(PageResources.Property.UsableArea)}
					value={unitFormatter(data.usableArea, UNITS.squareMeter)}
				/>
				<DetailRow
					label={t(PageResources.Property.LandArea)}
					value={unitFormatter(data.landSize, UNITS.squareMeter)}
				/>
				<DetailRow
					label={t(PageResources.Property.Ownership)}
					value={ownership}
				/>
				<PropertyValueRows
					reproductionValue={data.reproductionValue}
					interiorValue={data.interiorValue}
					currencySign={currencySign}
				/>
				<PropertyExtendedParams
					noun="realEstate"
					values={[
						unitFormatter(data.floorArea, UNITS.squareMeter),
						unitFormatter(data.secondaryBuildingArea, UNITS.squareMeter),
						buildingMaterial,
						data.numberOfFloors,
						properties,
					]}
				>
					<DetailRow
						label={t(PageResources.Property.FloorArea)}
						value={unitFormatter(data.floorArea, UNITS.squareMeter)}
					/>
					<DetailRow
						label={t(PageResources.Property.SecondaryBuildingArea)}
						value={unitFormatter(data.secondaryBuildingArea, UNITS.squareMeter)}
					/>
					<DetailRow
						label={t(PageResources.Property.BuildingMaterial)}
						value={buildingMaterial}
					/>
					<DetailRow
						label={t(PageResources.Property.NumberOfAboveGroundFloors)}
						value={data?.numberOfFloors}
					/>
					<DetailNote
						label={t(PageResources.Property.Properties)}
						text={properties}
					/>
				</PropertyExtendedParams>
			</Stack>

			<PropertyPurchaseDetails
				purchaseDate={data.purchaseDate}
				description={data.description}
				lastValuationDate={data.estimatedPriceAt}
			/>
		</>
	);
};
